import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaHeart,
  FaMinus,
  FaPlus,
  FaRegHeart,
  FaShieldAlt,
  FaShoppingCart,
  FaTruck,
  FaUndoAlt,
} from "react-icons/fa";
import productData from "../Datas/productData";
import { CartContext } from "./CartContext";
import "../css/productdetails.scss";

const getPrice = (product) => Number.parseFloat(String(product.price).replace(/,/g, "")) || 0;

const ProductDetail = () => {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, favorites, toggleFavorite } = useContext(CartContext);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState(product?.size?.[0] || "");
  const [selectedColor, setSelectedColor] = useState(product?.colors?.[0] || "");
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("description");
  const [added, setAdded] = useState(false);

  const related = useMemo(() => {
    if (!product) return [];
    return productData.filter((item) => item.category === product.category && item.id !== product.id).slice(0, 4);
  }, [product]);

  useEffect(() => {
    setSelectedImage(0);
    setSelectedSize(product?.size?.[0] || "");
    setSelectedColor(product?.colors?.[0] || "");
    setQuantity(1);
  }, [product]);

  if (!product) {
    return (
      <main className="product-detail product-detail--missing">
        <div>
          <span>404</span>
          <h1>Product not found</h1>
          <p>The product you’re looking for is no longer available or the address has changed.</p>
          <Link to="/shop">RETURN TO SHOP</Link>
        </div>
      </main>
    );
  }

  const images = product.images?.length ? product.images : [product.image];
  const price = getPrice(product);
  const isFavorite = favorites.some((item) => item.id === product.id);

  const setImage = (index) => {
    setSelectedImage((index + images.length) % images.length);
  };

  const handleAddToCart = () => {
    addToCart(product.id, quantity, selectedSize, selectedColor);
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1800);
  };

  return (
    <main className="product-detail">
      <div className="product-detail__breadcrumbs product-detail__container">
        <Link to="/">Home</Link><FaChevronRight />
        <Link to="/shop">Shop</Link><FaChevronRight />
        <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>{product.category}</Link><FaChevronRight />
        <span>{product.name}</span>
      </div>

      <section className="product-detail__container product-detail__overview">
        <div className="product-gallery">
          <div className="product-gallery__thumbs">
            {images.map((image, index) => (
              <button type="button" className={selectedImage === index ? "is-active" : ""} onClick={() => setImage(index)} key={image}>
                <img src={image} alt={`${product.name} view ${index + 1}`} />
              </button>
            ))}
          </div>
          <div className="product-gallery__main">
            <span>SALE</span>
            <img src={images[selectedImage]} alt={product.name} />
            {images.length > 1 && (
              <>
                <button type="button" className="product-gallery__previous" onClick={() => setImage(selectedImage - 1)} aria-label="Previous image"><FaChevronLeft /></button>
                <button type="button" className="product-gallery__next" onClick={() => setImage(selectedImage + 1)} aria-label="Next image"><FaChevronRight /></button>
              </>
            )}
          </div>
        </div>

        <div className="product-summary">
          <span className="product-summary__category">{product.category}</span>
          <h1>{product.name}</h1>
          <div className="product-summary__rating"><span>★★★★★</span><a href="#reviews">0 customer reviews</a></div>
          <div className="product-summary__price"><del>${(price * 1.18).toFixed(2)}</del><strong>${price.toFixed(2)}</strong></div>
          <p className="product-summary__intro">{product.description?.[0]}</p>
          <div className="product-summary__availability"><FaCheck /><span><strong>In stock</strong> — ships within 1–2 business days</span></div>

          {product.colors?.length > 0 && (
            <div className="product-option">
              <div><strong>Color</strong><span>{selectedColor}</span></div>
              <div className="product-option__values">
                {product.colors.map((color, index) => (
                  <button
                    type="button"
                    className={selectedColor === color ? "is-active" : ""}
                    onClick={() => setSelectedColor(color)}
                    key={color}
                  >
                    <i style={{ backgroundColor: index % 2 ? "#f2f2f2" : "#252525" }} />{color}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.size?.length > 1 && (
            <div className="product-option">
              <div><strong>Size</strong><span>{selectedSize}</span></div>
              <div className="product-option__values">
                {product.size.map((size) => (
                  <button type="button" className={selectedSize === size ? "is-active" : ""} onClick={() => setSelectedSize(size)} key={size}>{size}</button>
                ))}
              </div>
            </div>
          )}

          <div className="product-summary__purchase">
            <div className="product-quantity">
              <button type="button" onClick={() => setQuantity(Math.max(1, quantity - 1))} aria-label="Decrease quantity"><FaMinus /></button>
              <span>{quantity}</span>
              <button type="button" onClick={() => setQuantity(quantity + 1)} aria-label="Increase quantity"><FaPlus /></button>
            </div>
            <button type="button" className={added ? "is-added" : ""} onClick={handleAddToCart}>
              {added ? <FaCheck /> : <FaShoppingCart />}{added ? "ADDED TO CART" : "ADD TO CART"}
            </button>
          </div>
          {added && <Link className="product-summary__cart-link" to="/cartpage">Product added. View shopping cart <FaChevronRight /></Link>}

          <button type="button" className={`product-summary__wishlist ${isFavorite ? "is-active" : ""}`} onClick={() => toggleFavorite(product)}>
            {isFavorite ? <FaHeart /> : <FaRegHeart />}{isFavorite ? "Remove from wishlist" : "Add to wishlist"}
          </button>

          <div className="product-summary__meta">
            <p><strong>SKU:</strong> DZ-{String(product.id).padStart(4, "0")}</p>
            <p><strong>Category:</strong> <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>{product.category}</Link></p>
            <p><strong>Material:</strong> {product.material}</p>
          </div>

          <div className="product-summary__services">
            <div><FaTruck /><span><strong>Fast delivery</strong>Tracked shipping on every order</span></div>
            <div><FaUndoAlt /><span><strong>30-day returns</strong>Simple, worry-free returns</span></div>
            <div><FaShieldAlt /><span><strong>Secure checkout</strong>Protected payment information</span></div>
          </div>
        </div>
      </section>

      <section className="product-information">
        <div className="product-detail__container">
          <div className="product-information__tabs" role="tablist">
            <button type="button" className={activeTab === "description" ? "is-active" : ""} onClick={() => setActiveTab("description")}>DESCRIPTION</button>
            <button type="button" className={activeTab === "details" ? "is-active" : ""} onClick={() => setActiveTab("details")}>ADDITIONAL INFORMATION</button>
            <button type="button" className={activeTab === "shipping" ? "is-active" : ""} onClick={() => setActiveTab("shipping")}>SHIPPING & RETURNS</button>
          </div>
          <div className="product-information__content">
            {activeTab === "description" && (
              <div className="product-description-panel">
                <div>
                  <span>DESIGNED FOR EVERYDAY PERFORMANCE</span>
                  <h2>Technology that fits naturally into your setup.</h2>
                  {product.description?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                </div>
                <div>
                  <h3>Product highlights</h3>
                  <ul>{product.Features?.map((feature) => <li key={feature}><FaCheck />{feature}</li>)}</ul>
                </div>
              </div>
            )}
            {activeTab === "details" && (
              <dl className="product-specifications">
                <div><dt>Category</dt><dd>{product.category}</dd></div>
                <div><dt>Material</dt><dd>{product.material}</dd></div>
                <div><dt>Available color</dt><dd>{product.colors?.join(", ")}</dd></div>
                <div><dt>Package</dt><dd>Device, charging or connection cable, quick-start guide</dd></div>
                <div><dt>Warranty</dt><dd>12-month limited product warranty</dd></div>
              </dl>
            )}
            {activeTab === "shipping" && (
              <div className="product-shipping-panel">
                <div><FaTruck /><h3>Fast, tracked delivery</h3><p>Orders are processed within 1–2 business days. Tracking details are emailed after dispatch.</p></div>
                <div><FaUndoAlt /><h3>30-day returns</h3><p>Return unused items in their original packaging within 30 days of delivery.</p></div>
                <div><FaShieldAlt /><h3>Protected packaging</h3><p>Every electronic item is inspected and securely packed before it leaves our warehouse.</p></div>
              </div>
            )}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="product-related product-detail__container">
          <div className="product-related__heading"><div><span>YOU MAY ALSO LIKE</span><h2>Related products</h2></div><Link to={`/shop?category=${encodeURIComponent(product.category)}`}>VIEW ALL <FaChevronRight /></Link></div>
          <div className="product-related__grid">
            {related.map((item) => (
              <article key={item.id}>
                <Link to={`/product/${item.id}`}><div><img src={item.image} alt={item.name} loading="lazy" /></div><h3>{item.name}</h3></Link>
                <span>{item.category}</span><strong>${getPrice(item).toFixed(2)}</strong>
              </article>
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default ProductDetail;
